// Shared business authority. Object access and optimistic concurrency are checked
// by the owning route; edit authority never depends on a person's identity.
const canReviseAtDecisionStage = ({ role, workflow } = {}) =>
  ['nwacadministrator', 'systemadministrator'].includes(String(role || '').toLowerCase().replace(/[ _-]/g, '')) &&
  (workflow?.currentStage || workflow?.current_stage) === 'nwac_review';

const revisionError = (code, message, status = 409) =>
  Object.assign(new Error(code), { code, publicMessage: message, status });

function assertDecisionRevisionAllowed({ role, workflow, expectedVersion } = {}) {
  if (!canReviseAtDecisionStage({ role, workflow })) {
    throw revisionError('decision_revision_forbidden', 'Only Decision Makers can revise an assessment awaiting their decision.', 403);
  }
  if (!expectedVersion) {
    throw revisionError('decision_revision_version_required', 'Reload the assessment before editing it.', 422);
  }
}

function buildDecisionRevisionEvidence({ workflow, actorStaffProfileId, actorName, versionNumber, previousVersionNumber, before, after, savedAt = new Date().toISOString() }) {
  const raw = workflow?.metadata || workflow?.metadata_json || {};
  const metadata = typeof raw === 'string' ? JSON.parse(raw) : raw;
  const earlier = metadata.decisionRevisions || [];
  const directAppeal = metadata.entryMode === 'appeal_direct' && Number(metadata.appealId) > 0;
  if (!after || (!directAppeal && (!previousVersionNumber || !before))) {
    throw revisionError('decision_revision_baseline_missing', 'The submitted assessment evidence must be available before saving a revision.');
  }
  const evidence = {
    versionNumber, previousVersionNumber,
    submittedVersionNumber: earlier[0]?.submittedVersionNumber || previousVersionNumber,
    actorStaffProfileId, actorName, savedAt,
    submittedByStaffProfileId: workflow.submitted_by_staff_profile_id ?? workflow.submittedByStaffProfileId,
    submittedAt: workflow.submitted_at ?? workflow.submittedAt,
    rmReviewedByStaffProfileId: workflow.rm_reviewed_by_staff_profile_id ?? workflow.rmReviewedByStaffProfileId,
    rmReviewedAt: workflow.rm_reviewed_at ?? workflow.rmReviewedAt,
    before, after,
  };
  if (directAppeal) {
    evidence.entryMode = 'appeal_direct';
    evidence.appealId = metadata.appealId;
    if (!previousVersionNumber || !before) evidence.baselineUnavailable = true;
  }
  return evidence;
}

const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
function decisionRevisionEvidenceHtml(evidence) {
  if (!evidence) return '';
  if (evidence.entryMode === 'appeal_direct') {
    return `<section style="border:1px solid #687078;padding:12px;margin:12px 0"><strong>Appeal reconsideration v${escape(evidence.versionNumber)}</strong><p>Prepared by ${escape(evidence.actorName)} on ${escape(evidence.savedAt)} for appeal ${escape(evidence.appealId)}.</p><p>The Decision Maker prepared this reconsideration directly. No caseworker submission or Regional Manager endorsement is attributed to this version. ${evidence.baselineUnavailable ? 'The original assessment packet was unavailable; no comparison to that packet is claimed.' : 'Earlier decisions remain separate evidence.'} Any final decision in this packet applies to the content shown here.</p></section>`;
  }
  return `<section style="border:1px solid #687078;padding:12px;margin:12px 0"><strong>Decision Maker revision v${escape(evidence.versionNumber)}</strong><p>Revised by ${escape(evidence.actorName)} on ${escape(evidence.savedAt)}.</p><p>The original submission and Regional Manager endorsement apply to v${escape(evidence.submittedVersionNumber)}. They do not endorse these later changes. The original assessment and each comparison remain separate records. Any decision in this packet applies to the revised content shown here.</p></section>`;
}

function assessmentBarrierSnapshot(metadata = {}) {
  const selected = new Set((Array.isArray(metadata.barriers) ? metadata.barriers : []).map(value => String(value).trim().toLowerCase()));
  const fields = {
    none: 'none', education: 'education', lack_marketable_skills: 'lack of marketable skills',
    lack_work_experience: 'lack of work experience', remoteness: 'remoteness',
    lack_transportation: 'lack of transportation', economic: 'economic', language: 'language',
    lack_labour_force_attachment: 'lack of labour force attachment', dependent_care: 'dependent care',
    health: 'physical, emotional, or mental health', other: 'other',
  };
  return Object.fromEntries(Object.entries(fields).map(([field, label]) => [`barriers_${field}`, selected.has(label)]));
}

module.exports = { canReviseAtDecisionStage, assertDecisionRevisionAllowed, buildDecisionRevisionEvidence, decisionRevisionEvidenceHtml, assessmentBarrierSnapshot, revisionError };
